import React, { Fragment, useEffect, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { getAdminUsageOverview } from "../../utils/adminApi";

function formatDate(value, includeTime = false) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("mk-MK", {
    dateStyle: "medium",
    ...(includeTime ? { timeStyle: "short" } : {}),
  }).format(date);
}

function fullName(firstName, lastName) {
  return [firstName, lastName].filter(Boolean).join(" ") || "-";
}

function numberValue(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function formatMoney(value, currency = "") {
  return `${new Intl.NumberFormat("mk-MK", { maximumFractionDigits: 2 }).format(numberValue(value))}${currency ? ` ${currency}` : ""}`;
}

function csvCell(value) {
  return `"${String(value ?? "").replaceAll('"', '""')}"`;
}

function SummaryCard({ label, value, helper }) {
  return (
    <Box className="admin-summary-tile">
      <Typography className="admin-summary-value">{numberValue(value)}</Typography>
      <Typography variant="body2" fontWeight={700}>
        {label}
      </Typography>
      {helper ? (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

function MiniStat({ label, value, detail }) {
  return (
    <Box className="admin-mini-stat">
      <Typography variant="h6">{numberValue(value)}</Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {detail ? <Typography variant="caption">{detail}</Typography> : null}
    </Box>
  );
}

function UserDetails({ user }) {
  const todoPercent = user.todos
    ? Math.round((numberValue(user.todos_completed) / numberValue(user.todos)) * 100)
    : 0;

  return (
    <Box className="admin-user-details">
      <Box className="admin-detail-grid">
        <Box className="admin-detail-panel">
          <Typography variant="subtitle1" fontWeight={700}>
            Корисник и свадба
          </Typography>
          <Stack spacing={0.6} mt={1}>
            <Typography variant="body2"><strong>Email:</strong> {user.email || "-"}</Typography>
            <Typography variant="body2"><strong>Телефон:</strong> {user.phone_number || "-"}</Typography>
            <Typography variant="body2"><strong>Невеста:</strong> {fullName(user.bride_name, user.bride_last_name)}</Typography>
            <Typography variant="body2"><strong>Младоженец:</strong> {fullName(user.groom_name, user.groom_last_name)}</Typography>
            <Typography variant="body2"><strong>Свадба:</strong> #{user.wedding_id || "-"}, {formatDate(user.wedding_date)}</Typography>
            <Typography variant="body2"><strong>Локација:</strong> {[user.venue_name, user.venue_city].filter(Boolean).join(", ") || "-"}</Typography>
          </Stack>
        </Box>

        <Box className="admin-detail-panel">
          <Typography variant="subtitle1" fontWeight={700}>
            Користење на организаторот
          </Typography>
          <Box className="admin-mini-stat-grid" mt={1}>
            <MiniStat label="Гости" value={user.guests} detail={`${numberValue(user.guests_seated)} седнати`} />
            <MiniStat label="Маси" value={user.tables} detail={`${numberValue(user.seats)} места`} />
            <MiniStat label="Фотографии" value={user.photos} />
            <MiniStat label="Омилени" value={user.favorites} detail={`${numberValue(user.favorite_venues)} места · ${numberValue(user.favorite_products)} услуги`} />
          </Box>
          <Stack spacing={0.5} mt={1.5}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption">Ангажираност</Typography>
              <Typography variant="caption" fontWeight={700}>{numberValue(user.engagement_score)} / 100</Typography>
            </Stack>
            <LinearProgress variant="determinate" value={numberValue(user.engagement_score)} color={user.engagement_score >= 60 ? "success" : "primary"} />
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption">Завршени задачи</Typography>
              <Typography variant="caption" fontWeight={700}>
                {numberValue(user.todos_completed)} / {numberValue(user.todos)} ({todoPercent}%)
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={todoPercent} />
          </Stack>
        </Box>

        <Box className="admin-detail-panel">
          <Typography variant="subtitle1" fontWeight={700}>
            Буџет и финансии
          </Typography>
          {user.finance?.configured ? (
            <Stack spacing={0.6} mt={1}>
              <Typography variant="body2">
                <strong>Буџет:</strong> {formatMoney(user.finance.total_budget, user.finance.currency)}
              </Typography>
              <Typography variant="body2"><strong>Распределено:</strong> {formatMoney(user.finance.allocated_total, user.finance.currency)}</Typography>
              <Typography variant="body2"><strong>Потрошено:</strong> {formatMoney(user.finance.expenses_total, user.finance.currency)}</Typography>
              <Typography variant="body2"><strong>За плаќање:</strong> {formatMoney(user.finance.outstanding_total, user.finance.currency)}</Typography>
              <Typography variant="body2"><strong>Категории:</strong> {numberValue(user.finance.categories)}</Typography>
              <Typography variant="body2"><strong>Понуди:</strong> {numberValue(user.finance.vendor_quotes)}</Typography>
              <Typography variant="body2"><strong>Плаќања:</strong> {numberValue(user.finance.payments)}</Typography>
              <Typography variant="body2"><strong>Трошоци:</strong> {numberValue(user.finance.expenses)}</Typography>
              {user.finance.overdue_payments ? <Chip size="small" color="warning" label={`${numberValue(user.finance.overdue_payments)} задоцнети плаќања`} /> : null}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Корисникот сè уште нема поставено буџет.
            </Typography>
          )}
        </Box>

        <Box className={`admin-detail-panel${user.needs_attention ? " admin-concern-panel" : ""}`}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>Сигнали и рецензија</Typography>
            {user.review ? (
              <Chip
                size="small"
                color={user.review.rating <= 2 ? "error" : "success"}
                label={`${numberValue(user.review.rating)} / 5`}
              />
            ) : null}
          </Stack>
          {user.review ? (
            <Stack spacing={0.8} mt={1}>
              <Typography variant="body2">“{user.review.comment}”</Typography>
              <Typography variant="caption" color="text.secondary">
                Ажурирана {formatDate(user.review.updated_at, true)}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" mt={1}>Нема оставено рецензија.</Typography>
          )}
          <Stack spacing={0.7} mt={1}>
            {user.concern_reasons?.map((reason) => (
              <Alert severity="warning" key={reason}>{reason}</Alert>
            ))}
            {!user.needs_attention ? <Alert severity="success">Нема автоматски сигнали за проверка.</Alert> : null}
          </Stack>
        </Box>
      </Box>

      <Box className="admin-detail-section">
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={0.5} mb={1}>
          <Typography variant="subtitle1" fontWeight={700}>Гости ({numberValue(user.guests)})</Typography>
          <Typography variant="caption" color="text.secondary">
            Потврдени {numberValue(user.guests_confirmed)} · Одбиени {numberValue(user.guests_rejected)} · Без одговор {numberValue(user.guests_unknown)}
          </Typography>
        </Stack>
        {user.guest_details?.length ? (
          <Box className="admin-nested-table-wrap">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Име</TableCell>
                  <TableCell>Страна</TableCell>
                  <TableCell>Однос</TableCell>
                  <TableCell>Возраст</TableCell>
                  <TableCell>RSVP</TableCell>
                  <TableCell>Седнат</TableCell>
                  <TableCell>Маса / место</TableCell>
                  <TableCell>Додаден</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user.guest_details.map((guest) => (
                  <TableRow key={guest.id}>
                    <TableCell>{fullName(guest.name, guest.last_name)}</TableCell>
                    <TableCell>{guest.side || "-"}</TableCell>
                    <TableCell>{guest.relationship_category || "-"}</TableCell>
                    <TableCell>{guest.age_category || "-"}</TableCell>
                    <TableCell>{guest.rsvp_status || "-"}</TableCell>
                    <TableCell>{guest.seated ? "Да" : "Не"}</TableCell>
                    <TableCell>{guest.table_name || "-"}{guest.seat_number ? ` / ${guest.seat_number}` : ""}</TableCell>
                    <TableCell>{formatDate(guest.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Нема додадени гости.</Typography>
        )}
      </Box>

      <Box className="admin-detail-section">
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Финансиски записи ({user.finance_details?.length || 0})
        </Typography>
        {user.finance_details?.length ? (
          <Box className="admin-nested-table-wrap">
            <Table size="small">
              <TableHead><TableRow><TableCell>Тип</TableCell><TableCell>Назив</TableCell><TableCell>Статус</TableCell><TableCell align="right">Износ</TableCell><TableCell>Датум</TableCell></TableRow></TableHead>
              <TableBody>
                {user.finance_details.map((item) => (
                  <TableRow key={`${item.item_type}-${item.id}`}>
                    <TableCell>{item.item_type}</TableCell>
                    <TableCell>{item.title}</TableCell>
                    <TableCell>{item.status || "-"}</TableCell>
                    <TableCell align="right">{formatMoney(item.amount, user.finance?.currency)}{item.secondary_amount !== null && item.secondary_amount !== undefined ? ` (${formatMoney(item.secondary_amount)} платено)` : ""}</TableCell>
                    <TableCell>{formatDate(item.date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : <Typography variant="body2" color="text.secondary">Нема финансиски записи.</Typography>}
      </Box>

      <Box className="admin-detail-section">
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Фотографии и омилени
        </Typography>
        <Box className="admin-artifact-grid">
          <Box className="admin-artifact-card">
            <Typography variant="subtitle2" mb={1}>Фотографии ({numberValue(user.photos)})</Typography>
            {user.photo_details?.length ? user.photo_details.map((photo) => (
              <Typography variant="body2" key={photo.id}>#{photo.id} · {photo.name || "Без име"}{photo.favorite ? " · ★" : ""}</Typography>
            )) : <Typography variant="body2" color="text.secondary">Нема фотографии.</Typography>}
          </Box>
          <Box className="admin-artifact-card">
            <Typography variant="subtitle2" mb={1}>Омилени ({numberValue(user.favorites)})</Typography>
            {user.favorite_details?.length ? user.favorite_details.map((favorite) => (
              <Typography variant="body2" key={favorite.id}>{favorite.favorite_type} · {favorite.item_id}</Typography>
            )) : <Typography variant="body2" color="text.secondary">Нема омилени.</Typography>}
          </Box>
        </Box>
      </Box>

      <Box className="admin-detail-section">
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Маси и распоред ({numberValue(user.tables)})
        </Typography>
        {user.table_details?.length ? (
          <Box className="admin-nested-table-wrap">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Маса</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell align="center">Капацитет</TableCell>
                  <TableCell align="center">Доделени места</TableCell>
                  <TableCell>Креирана</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user.table_details.map((table) => (
                  <TableRow key={table.id}>
                    <TableCell>{table.name || `Маса ${table.number || table.id}`}</TableCell>
                    <TableCell>{table.table_type || "-"}</TableCell>
                    <TableCell align="center">{numberValue(table.seat_count)}</TableCell>
                    <TableCell align="center">{numberValue(table.assigned_seats)}</TableCell>
                    <TableCell>{formatDate(table.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Нема креирани маси.</Typography>
        )}
      </Box>

      <Box className="admin-detail-section">
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Задачи ({numberValue(user.todos_completed)} / {numberValue(user.todos)})
        </Typography>
        {user.todo_details?.length ? (
          <Box className="admin-todo-list">
            {user.todo_details.map((todo) => (
              <Box className="admin-todo-row" key={todo.id}>
                <Chip size="small" color={todo.checked ? "success" : "default"} label={todo.checked ? "Завршена" : "Отворена"} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>{todo.title || `Задача #${todo.id}`}</Typography>
                  {todo.description ? <Typography variant="caption" color="text.secondary">{todo.description}</Typography> : null}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Нема задачи.</Typography>
        )}
      </Box>

      <Box className="admin-detail-section">
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Покани ({numberValue(user.invitations)})
        </Typography>
        {user.invitation_details?.length ? (
          <Box className="admin-artifact-grid">
            {user.invitation_details.map((invitation) => (
              <Box className="admin-artifact-card" key={invitation.id}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="subtitle2">{invitation.title}</Typography>
                  <Chip size="small" label={invitation.is_public ? "Јавна" : "Приватна"} color={invitation.is_public ? "success" : "default"} variant="outlined" />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {formatDate(invitation.event_date)} · {invitation.venue_name || "Без локација"}
                </Typography>
                {invitation.message ? <Typography variant="body2" mt={1}>“{invitation.message}”</Typography> : null}
                <Typography variant="body2" mt={1}>
                  {numberValue(invitation.invited_guests)} поканети · {numberValue(invitation.rsvps)} RSVP · {numberValue(invitation.attending_people)} присутни
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Да {numberValue(invitation.yes_responses)} · Не {numberValue(invitation.no_responses)} · Можеби {numberValue(invitation.maybe_responses)} · {numberValue(invitation.agenda_items)} настани
                </Typography>
                {invitation.guest_details?.length ? (
                  <Box className="admin-compact-records" mt={1}>
                    <Typography variant="caption" fontWeight={700}>Листа за покана</Typography>
                    {invitation.guest_details.map((guest) => <Typography variant="caption" display="block" key={guest.id}>{guest.name} · {guest.rsvp_status || "pending"}{guest.email ? ` · ${guest.email}` : ""}</Typography>)}
                  </Box>
                ) : null}
                {invitation.rsvp_details?.length ? (
                  <Box className="admin-compact-records" mt={1}>
                    <Typography variant="caption" fontWeight={700}>Одговори</Typography>
                    {invitation.rsvp_details.map((rsvp) => <Typography variant="caption" display="block" key={rsvp.id}>{rsvp.name} · {rsvp.response} · {numberValue(rsvp.number_of_people)} лица{rsvp.message ? ` · “${rsvp.message}”` : ""}</Typography>)}
                  </Box>
                ) : null}
                {invitation.is_public ? (
                  <Link href={`/invite/${invitation.slug}`} target="_blank" rel="noreferrer" variant="caption">
                    Отвори ја поканата
                  </Link>
                ) : null}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Нема креирани покани.</Typography>
        )}
      </Box>

      <Box className="admin-detail-section">
        <Typography variant="subtitle1" fontWeight={700} mb={1}>
          Save the date ({numberValue(user.save_the_dates)})
        </Typography>
        {user.save_the_date_details?.length ? (
          <Box className="admin-artifact-grid">
            {user.save_the_date_details.map((item) => (
              <Box className="admin-artifact-card" key={item.id}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="subtitle2">{item.title}</Typography>
                  <Chip size="small" label={item.is_public ? "Јавна" : "Приватна"} color={item.is_public ? "success" : "default"} variant="outlined" />
                </Stack>
                <Typography variant="body2">{formatDate(item.event_date)} · {item.location}</Typography>
                {item.message ? <Typography variant="body2" mt={1}>“{item.message}”</Typography> : null}
                <Stack direction="row" spacing={1} mt={1}>
                  <Link href={item.cover_image_url} target="_blank" rel="noreferrer" variant="caption">Cover</Link>
                  <Link href={item.footer_image_url} target="_blank" rel="noreferrer" variant="caption">Footer</Link>
                </Stack>
                {item.is_public ? (
                  <Link href={`/save-the-dates/${item.slug}`} target="_blank" rel="noreferrer" variant="caption">
                    Отвори ја објавата
                  </Link>
                ) : null}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">Нема креирани Save the date објави.</Typography>
        )}
      </Box>
    </Box>
  );
}

function AdminUsageOverviewSection() {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("activity");
  const [attentionFilter, setAttentionFilter] = useState("all");
  const [weddingFilter, setWeddingFilter] = useState("all");
  const [expandedUserId, setExpandedUserId] = useState(null);

  const loadOverview = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getAdminUsageOverview();
      setOverview(data);
    } catch (err) {
      console.error(err);
      setError(err.message || "Неуспешно вчитување на админ прегледот.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("mk-MK");
    const users = Array.isArray(overview?.users) ? [...overview.users] : [];
    const searched = query
      ? users.filter((user) =>
          [
            user.id,
            user.email,
            user.phone_number,
            user.wedding_id,
            user.bride_name,
            user.bride_last_name,
            user.groom_name,
            user.groom_last_name,
            user.venue_name,
            user.review?.comment,
            user.invitation_details?.map((item) => item.title).join(" "),
            user.save_the_date_details?.map((item) => item.title).join(" "),
            user.todo_details?.map((item) => item.title).join(" "),
            user.photo_details?.map((item) => item.name).join(" "),
            user.favorite_details?.map((item) => item.item_id).join(" "),
          ]
            .filter((value) => value !== null && value !== undefined)
            .join(" ")
            .toLocaleLowerCase("mk-MK")
            .includes(query)
        )
      : users;

    const now = Date.now();
    const filtered = searched.filter((user) => {
      if (attentionFilter === "attention" && !user.needs_attention) return false;
      if (attentionFilter === "clear" && user.needs_attention) return false;
      const weddingTime = user.wedding_date ? new Date(user.wedding_date).getTime() : null;
      if (weddingFilter === "upcoming" && (!weddingTime || weddingTime < now)) return false;
      if (weddingFilter === "past" && (!weddingTime || weddingTime >= now)) return false;
      if (weddingFilter === "undated" && weddingTime) return false;
      return true;
    });

    return filtered.sort((left, right) => {
      if (sort === "registered") {
        return new Date(right.created_at).getTime() - new Date(left.created_at).getTime();
      }
      if (sort === "guests") {
        return numberValue(right.guests) - numberValue(left.guests);
      }
      if (sort === "invitations") {
        return numberValue(right.invitations) - numberValue(left.invitations);
      }
      if (sort === "engagement") {
        return numberValue(right.engagement_score) - numberValue(left.engagement_score);
      }
      if (sort === "created") {
        return numberValue(right.created_items) - numberValue(left.created_items);
      }
      if (sort === "concerns") {
        return numberValue(right.needs_attention) - numberValue(left.needs_attention)
          || numberValue(left.review?.rating) - numberValue(right.review?.rating);
      }
      return new Date(right.last_activity_at).getTime() - new Date(left.last_activity_at).getTime();
    });
  }, [attentionFilter, overview, search, sort, weddingFilter]);

  const exportUsers = () => {
    const headings = ["ID", "Email", "Телефон", "Свадба", "Датум", "Локација", "Последна активност", "Ангажираност", "Креирани ставки", "Гости", "Маси", "Покани", "RSVP", "Фотографии", "Омилени", "Оценка", "За проверка"];
    const rows = visibleUsers.map((user) => [user.id, user.email, user.phone_number, user.wedding_id, user.wedding_date, user.venue_name, user.last_activity_at, user.engagement_score, user.created_items, user.guests, user.tables, user.invitations, user.invitation_rsvps, user.photos, user.favorites, user.review?.rating, user.concern_reasons?.join("; ")]);
    const csv = [headings, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `wedding-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const summary = overview?.summary || {};

  return (
    <Card className="admin-card admin-overview-card">
      <CardContent>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.5} mb={2}>
          <Box>
            <Typography variant="h5" component="h2">Преглед на користење</Typography>
            <Typography variant="body2" color="text.secondary">
              Свадби, гости, покани и активност по корисник.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportUsers} disabled={!overview}>CSV</Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadOverview} disabled={loading}>Освежи</Button>
          </Stack>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {loading && !overview ? (
          <Box className="admin-loading-wrap"><CircularProgress size={28} /></Box>
        ) : (
          <>
            <Box className="admin-summary-grid">
              <SummaryCard label="Корисници" value={summary.users} helper={`${numberValue(summary.verified_users)} потврдени`} />
              <SummaryCard label="Свадби" value={summary.weddings} helper={`${numberValue(summary.upcoming_weddings)} претстојни`} />
              <SummaryCard label="Гости" value={summary.guests} helper={`${numberValue(summary.seated_guests)} седнати`} />
              <SummaryCard label="Покани" value={summary.invitations} helper={`${numberValue(summary.invitation_guests)} поканети`} />
              <SummaryCard label="RSVP одговори" value={summary.invitation_rsvps} helper={`${numberValue(summary.attending_people)} присутни`} />
              <SummaryCard label="Save the date" value={summary.save_the_dates} />
              <SummaryCard label="Фотографии" value={summary.photos} />
              <SummaryCard label="Активни буџети" value={summary.finance_users} />
              <SummaryCard label="Рецензии" value={summary.reviews} helper={`Просек ${numberValue(summary.average_rating).toFixed(1)} / 5`} />
              <SummaryCard label="Активни 30 дена" value={summary.active_users_30_days} />
              <SummaryCard label="Креирани ставки" value={summary.total_created_items} />
              <SummaryCard label="Просечна ангажираност" value={summary.average_engagement_score} helper="од 100" />
              <SummaryCard label="За проверка" value={summary.users_needing_attention} helper={`${numberValue(summary.reviews_needing_attention)} ниски оценки`} />
            </Box>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} my={2}>
              <TextField
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Пребарај email, име, телефон, свадба или локација"
                size="small"
                fullWidth
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
              />
              <TextField select SelectProps={{ native: true }} size="small" label="Подреди" value={sort} onChange={(event) => setSort(event.target.value)} className="admin-sort-field">
                <option value="activity">Последна активност</option>
                <option value="registered">Најнови корисници</option>
                <option value="guests">Најмногу гости</option>
                <option value="invitations">Најмногу покани</option>
                <option value="concerns">Прво загрижености</option>
                <option value="engagement">Најголема ангажираност</option>
                <option value="created">Најмногу креирани ставки</option>
              </TextField>
              <TextField select SelectProps={{ native: true }} size="small" label="Сигнали" value={attentionFilter} onChange={(event) => setAttentionFilter(event.target.value)} className="admin-filter-field">
                <option value="all">Сите</option><option value="attention">За проверка</option><option value="clear">Без сигнали</option>
              </TextField>
              <TextField select SelectProps={{ native: true }} size="small" label="Свадба" value={weddingFilter} onChange={(event) => setWeddingFilter(event.target.value)} className="admin-filter-field">
                <option value="all">Сите</option><option value="upcoming">Претстојни</option><option value="past">Поминати</option><option value="undated">Без датум</option>
              </TextField>
            </Stack>

            <Typography variant="caption" color="text.secondary" display="block" mb={1}>
              Прикажани {visibleUsers.length} од {numberValue(summary.users)} корисници · Последно освежено {formatDate(overview?.generated_at, true)}
            </Typography>

            <Box className="admin-table-wrap">
              <Table size="small" className="admin-usage-table">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Корисник</TableCell>
                    <TableCell>Свадба</TableCell>
                    <TableCell>Регистриран / активен</TableCell>
                    <TableCell align="center">Ангажираност</TableCell>
                    <TableCell align="center">Гости</TableCell>
                    <TableCell align="center">Покани / RSVP</TableCell>
                    <TableCell align="center">Маси</TableCell>
                    <TableCell align="center">Задачи</TableCell>
                    <TableCell align="center">Другo</TableCell>
                    <TableCell align="center">Рецензија</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleUsers.map((user) => {
                    const expanded = expandedUserId === user.id;
                    return (
                      <Fragment key={user.id}>
                        <TableRow hover className={expanded ? "admin-user-row-expanded" : ""}>
                          <TableCell padding="checkbox">
                            <Tooltip title={expanded ? "Собери детали" : "Прикажи детали"}>
                              <IconButton size="small" onClick={() => setExpandedUserId(expanded ? null : user.id)} aria-label={`${expanded ? "Собери" : "Прикажи"} детали за ${user.email}`} aria-expanded={expanded}>
                                <ExpandMoreIcon className={expanded ? "admin-expand-icon admin-expand-icon-open" : "admin-expand-icon"} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>{user.email || `User #${user.id}`}</Typography>
                            <Typography variant="caption" color="text.secondary">#{user.id} · {user.phone_number || "без телефон"}</Typography>
                            <Box mt={0.5}>
                              {user.email_verified ? <Chip label="Потврден" color="success" variant="outlined" size="small" /> : <Chip label="Непотврден" color="warning" variant="outlined" size="small" />}
                            </Box>
                          </TableCell>
                          <TableCell align="center"><strong>{numberValue(user.engagement_score)}%</strong><Typography variant="caption" display="block" color="text.secondary">{numberValue(user.created_items)} ставки</Typography></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>#{user.wedding_id || "-"}</Typography>
                            <Typography variant="caption" display="block">{fullName(user.bride_name, user.bride_last_name)} &amp; {fullName(user.groom_name, user.groom_last_name)}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDate(user.wedding_date)} · {user.venue_name || "без локација"}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" display="block">Креиран: {formatDate(user.created_at)}</Typography>
                            <Typography variant="caption" color="text.secondary">Активен: {formatDate(user.last_activity_at, true)}</Typography>
                          </TableCell>
                          <TableCell align="center"><strong>{numberValue(user.guests)}</strong><Typography variant="caption" display="block" color="text.secondary">{numberValue(user.guests_seated)} седнати</Typography></TableCell>
                          <TableCell align="center"><strong>{numberValue(user.invitations)} / {numberValue(user.invitation_rsvps)}</strong><Typography variant="caption" display="block" color="text.secondary">{numberValue(user.invitation_attending_people)} присутни</Typography></TableCell>
                          <TableCell align="center"><strong>{numberValue(user.tables)}</strong><Typography variant="caption" display="block" color="text.secondary">{numberValue(user.seats)} места</Typography></TableCell>
                          <TableCell align="center"><strong>{numberValue(user.todos_completed)} / {numberValue(user.todos)}</strong></TableCell>
                          <TableCell align="center"><Typography variant="caption" display="block">{numberValue(user.photos)} фото</Typography><Typography variant="caption" display="block">{numberValue(user.save_the_dates)} STD</Typography><Typography variant="caption" display="block">{numberValue(user.favorites)} омилени</Typography></TableCell>
                          <TableCell align="center">
                            {user.review ? (
                              <Chip
                                size="small"
                                label={`${numberValue(user.review.rating)} / 5`}
                                color={user.review.rating <= 2 ? "error" : "success"}
                                variant={user.review.rating <= 2 ? "filled" : "outlined"}
                              />
                            ) : <Typography variant="caption" color="text.secondary">-</Typography>}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell className="admin-collapse-cell" colSpan={11}>
                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                              <UserDetails user={user} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    );
                  })}
                  {!visibleUsers.length ? (
                    <TableRow><TableCell colSpan={11}>Нема корисници што одговараат на пребарувањето.</TableCell></TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default AdminUsageOverviewSection;
